// lib
import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT } from '@aytm/lib/common/assets'
import { configure as configureImageAssetUploadManager } from '@aytm/lib/rfd-ui/components/assets/jobs/image-asset-upload-manager'

// components
import { Button } from 'primereact/button'

import ImagePreview from '@aytm/app/stats/components/image-preview/ImagePreview'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import UploadAssetsButton from '@aytm/lib/rfd-ui/components/assets/upload-assets-button'

import QuestionHeader from '@aytm/app/stats/page/question-views/components/QuestionHeader'
import QuestionBody from '@aytm/app/stats/page/question-views/components/QuestionBody'
import QuestionAnnotation from '@aytm/app/stats/page/question-views/components/QuestionAnnotation'

// styles
import style from '@aytm/app/stats/page/question-views/sn/styles/Component.module.scss'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import QuestionFooter from '@aytm/app/stats/page/question-views/components/QuestionFooter'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// Read at a glance and dated in full on hover. The steps and the wording follow the report popup's
// own approximation, which is the reading a reader of this page already knows; both are read in the
// account's timezone rather than the machine's, since the times themselves are written server-side.
const localised = at => {
  const zone = PageGlobals.TIME_ZONE || {}
  return moment(at).utcOffset(Object.hasOwn(zone, 'offset') ? zone.offset : moment().utcOffset())
}

export const editedAgo = at => {
  const then = localised(at)
  const minutes = moment().diff(then, 'minutes')
  if (1 > minutes) return 'just now'
  if (60 > minutes) return `${minutes} minute${1 === minutes ? '' : 's'} ago`

  const hours = moment().diff(then, 'hours')
  if (24 > hours) return `${hours} hour${1 === hours ? '' : 's'} ago`

  const days = moment().diff(then, 'days')
  if (31 > days) return `${days} day${1 === days ? '' : 's'} ago`

  const months = moment().diff(then, 'months')
  if (12 > months) return `${months} month${1 === months ? '' : 's'} ago`

  const years = moment().diff(then, 'years')
  return `${years} year${1 === years ? '' : 's'} ago`
}

// `YYYY` -- the calendar year. `GGGG` is the ISO week-year, which reads a year out on the days either
// side of New Year; the editor's own comments carry that defect and it is not worth spreading.
export const editedOn = at => `${localised(at).format('MMM DD, YYYY')} at ${localised(at).format('hh:mm a')}`

// Skipper writes a note rather than editing one, and the report attributes it as it generates it.
// Paired with AUTHOR in the report builder that stamps those notes -- the two must stay the same word
// or a Skipper note quietly reads as a person's.
const AI_AUTHOR = 'Skipper'

const Component = ({ model, features, isQuestionCollapsed, onChange, onAIAddQuestionClick, ...rest }) => {
  const [title, setTitle] = useState(model.text)
  const [asset, setAsset] = useState(model.asset)
  const [note, setNote] = useState(model.note || '')

  const [collapsed, setCollapsed] = useState(isQuestionCollapsed)

  const [loaded, setLoaded] = useState(false)

  const [editing, setEditing] = useState(Boolean(model.justAdded))
  const [uploading, setUploading] = useState(false)

  const [errorText, setErrorText] = useState(null)

  const timer = useRef(null)

  const imageRef = useRef()

  const savedValues = useRef(null)
  savedValues.current = { title: model.text, note: model.note, asset: model.asset }

  const hasImage = asset && Object.hasOwn(asset, 'path')
  const { disabled } = features

  const isSaveDisabled =
    disabled ||
    uploading ||
    title == undefined ||
    !title.trim().length ||
    (title === savedValues.current.title &&
      (note || '') === (model.note || '') &&
      asset?.uid === savedValues.current.asset?.uid)

  configureImageAssetUploadManager({
    createAssetPath: 'report/image_asset/create'
  })

  const onUploadAsset = res => {
    setUploading(false)

    if (res && res[0]) {
      const asset = res[0]
      const showError = asset && ['invalid', 'error', 'failed'].includes(asset.status)
      if (showError) {
        const error = asset.error?.length
          ? asset.error.join('<br />')
          : "We couldn't upload your file, please check your connection, file and try again. 30MB max."
        setErrorText(error)
      } else {
        const image = {
          uid: asset.assetUid,
          url: asset.imageUrl,
          path: asset.imagePath,
          original: asset.original
        }

        setErrorText(null)
        setAsset(image)
      }
    }
  }

  const onDeleteAsset = () => {
    setAsset(null)
  }

  const onChangeText = html => {
    setNote(html || '')
  }

  const onStartEditing = () => {
    setEditing(true)
  }

  const onCancel = () => {
    setTitle(savedValues.current.title)
    setAsset(savedValues.current.asset)
    setNote(savedValues.current.note || '')
    setUploading(false)
    setErrorText(null)
    setEditing(false)
  }

  // the header cancels on any Escape, and the one that closes the full-screen viewer must not
  const viewerOpen = useRef(false)

  const onSetEditing = isEdit => {
    if (isEdit) onStartEditing()
    else if (!viewerOpen.current) onCancel()
  }

  const onSave = (withThisNote = note) => {
    if (
      title !== savedValues.current.title ||
      withThisNote !== model.note ||
      asset?.uid !== savedValues.current.asset?.uid
    ) {
      const updatedNote = withThisNote.replace(/^(?:\s*<br\b[^>]*>\s*)+|(?:\s*<br\b[^>]*>\s*)+$/gi, '').trim()
      setNote(updatedNote)
      onChange({
        text: title,
        note: updatedNote,
        asset,
        updatedAt: moment().toISOString(),
        updatedBy: PageGlobals.USERNAME || null
      })
    }
    setUploading(false)
    setEditing(false)
    setErrorText(null)
  }

  const onImageLoaded = () => setLoaded(true)

  useEffect(() => {
    setTitle(model.text)
    setAsset(model.asset)
    setNote(model.note || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.text, model.asset?.uid, model.note])

  useEffect(() => {
    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => setCollapsed(isQuestionCollapsed), isQuestionCollapsed ? 600 : 0)

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionCollapsed])

  const renderImage = () =>
    hasImage ? (
      <div data-testid={`stat-storytelling-image-${model.id}`} className={`${style.row} ${style['row-image']}`}>
        {editing && !uploading ? (
          <span
            data-testid={`stat-storytelling-delete-asset-${model.id}`}
            className={style.deleteAsset}
            aria-label="Delete image"
            {...pressable(onDeleteAsset)}
          >
            <i className="icon-aytmfd-trash" />
          </span>
        ) : null}
        <ImagePreview
          ref={imageRef}
          className={loaded ? '' : 'p-hidden'}
          maxWidth="100%"
          maxHeight="600px"
          imageUrl={asset.original?.url || asset.url}
          previewUrl={getImageAssetPath({
            path: asset.original?.path || asset.path,
            width: 1000,
            height: 1000,
            filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
            backgroundColor: 'transparent',
            unsharp: true
          })}
          onImageLoaded={onImageLoaded}
          onImageOpen={() => (viewerOpen.current = true)}
          onImageHide={() => (viewerOpen.current = false)}
        />
      </div>
    ) : null

  // The block's own footer zone, which a note has no total to fill. Rendered even with nothing to
  // report, so a note that has never been written stands at its full height from the start rather
  // than growing one the first time it is saved.
  const renderLastEdited = () => (
    <span data-testid="stat-sn-last-edited" className={style.lastEdited}>
      {model.updatedAt ? (
        <>
          {model.updatedBy ? (
            <>
              {AI_AUTHOR === model.updatedBy ? 'Written by ' : 'Last edited by '}
              <b>{model.updatedBy}</b>{' '}
            </>
          ) : (
            'Last edited '
          )}
          {/* the full date is what the reading of the time expands on, so it is declared on that word */}
          <b data-testid="stat-sn-last-edited-at" title={editedOn(model.updatedAt)}>
            {editedAgo(model.updatedAt)}
          </b>
        </>
      ) : (
        <>&nbsp;</>
      )}
    </span>
  )

  return (
    <div className="question-container">
      <QuestionHeader
        // className={style.block}
        // the pencil reads the comment off this key, which the chart questions' annotation fills and a Note never does;
        // a Note is never summarised, and a hash left undefined makes the header cancel editing on mount
        model={{ ...model, storytellingNote: model.note || '', aiAssistantsHash: null }}
        features={features}
        titleExt={title}
        isEditingExt={editing}
        isQuestionCollapsed={collapsed}
        onSetTitleExt={setTitle}
        onSetEditQuestionExt={onSetEditing}
        onSaveExt={onSave}
        {...rest}
      />
      {collapsed ? null : (
        <QuestionBody editing={editing} isQuestionCollapsed={isQuestionCollapsed}>
          <BlockWrapper className={style.block}>
            <div className={style.container}>
              {editing ? (
                <>
                  <div className={style.row}>
                    <div className={`${style.help} p-col`}>
                      Type in/paste your executive summary below. It will be added as a slide on PPTX exports.
                    </div>
                    <div data-testid={`stat-storytelling-change-asset-${model.id}`} className={`${style.changeAsset} p-right p-col-4`}>
                      <UploadAssetsButton
                        type="image"
                        text={hasImage ? 'Replace illustration' : 'Add an illustration'}
                        withOriginal
                        withErrors
                        renderProgressText={() => 'Uploading your image'}
                        onFilesUpload={() => setUploading(true)}
                        onFilesUploaded={onUploadAsset}
                      />
                    </div>
                  </div>
                  {errorText ? (
                    <div
                      data-testid={`stat-storytelling-upload-error-${model.id}`}
                      className={`${style.row} ${style.error}`}
                      dangerouslySetInnerHTML={{ __html: errorText }}
                    />
                  ) : null}
                </>
              ) : null}
              {renderImage()}
              {editing ? (
                disabled ? (
                  <>
                    {0 < note?.length ? (
                      <QuestionAnnotation
                        id={model.id}
                        className={style.annotation}
                        note={note || ''}
                        editing={false}
                        isNoteEditable={false}
                        disabled
                      />
                    ) : null}
                    <div className={`${style.row} p-col-12`}>
                      <div className="p-field p-col">&nbsp;</div>
                      <div className={`${style.group} p-fluid p-right p-col-4`}>
                        <Button
                          id={`stat-annotation-cancel-button-${model.id}`}
                          className={classNames(
                            'stat-annotation-cancel-button -dtr-stat-annotation-cancel-button',
                            style.button,
                            'p-button-rounded p-button-blue p-button-secondary'
                          )}
                          label="Cancel"
                          onClick={onCancel}
                        />
                        <Button
                          id={`stat-annotation-save-button-${model.id}`}
                          className={classNames(
                            'stat-annotation-save-button -dtr-stat-annotation-save-button',
                            style.button,
                            'p-button-rounded p-button-blue'
                          )}
                          label="Save"
                          disabled={true}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <QuestionAnnotation
                    id={model.id}
                    className={style['annotation-editing']}
                    note={note || ''}
                    editing={editing}
                    isNoteEditable={!disabled}
                    isSaveDisabled={isSaveDisabled}
                    disabled={disabled}
                    setNote={onChangeText}
                    onSaveEditing={onSave}
                    onCancelEditing={onCancel}
                    onAIAddQuestionClick={onAIAddQuestionClick}
                  />
                )
              ) : 0 < note?.length ? (
                <QuestionAnnotation
                  className={style.annotation}
                  id={model.id}
                  note={note || ''}
                  editing={false}
                  isNoteEditable={false}
                  disabled
                  onStartEditing={disabled ? null : onStartEditing}
                  onAIAddQuestionClick={onAIAddQuestionClick}
                />
              ) : hasImage || disabled ? null : (
                <div className={style.invitation} onDoubleClick={disabled ? null : onStartEditing}>
                  Add an executive summary/notes to your report.
                </div>
              )}
            </div>
          </BlockWrapper>
          <QuestionFooter footer={renderLastEdited()} />
        </QuestionBody>
      )}
    </div>
  )
}

Component.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
    text: PropTypes.string,
    asset: PropTypes.object,
    note: PropTypes.string,
    justAdded: PropTypes.bool,
    updatedAt: PropTypes.string,
    updatedBy: PropTypes.string
  }),
  features: PropTypes.object,
  isQuestionCollapsed: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onAIAddQuestionClick: PropTypes.func
}

Component.displayName = 'Component'

export default Component
